// The Research tab's feed (Phase 4, item 6): every dated thing we learned about the company,
// grouped This week / Last 30 days / Earlier, with a source filter. Pure, so `node --test`
// covers it.
//
// Sources: Scout's saved company research (#427), the weighed signals (best_signal.py, which
// carry the Q4 research's why-now as its own source), the
// Clay signal's sightings and summary, the Clay engine's tier read, and account triage's opener
// and use case. Activity, meetings and deals are History's, not this feed's.
import type { BookAccount } from '../api/types.ts';
import { daysSince, humanValue, pacificCalendarDay } from '../lib/format.ts';
import { signalRange, signalTypes, summaryParts, tierAdjustments, tierBand, verdictSentence } from './clayEngine.ts';
import { safeHref, sourceHost } from './overviewSource.ts';
import { isQ4Signal, q4SignalLabel, withoutQ4Prefix } from '../lib/q4Target.ts';

export interface ResearchItem {
  source: string;
  // YYYY-MM-DD, or null when the fact carries no date.
  day: string | null;
  text: string;
  url: string | null;
}

export const RESEARCH_GROUPS = ['This week', 'Last 30 days', 'Earlier'] as const;
export type ResearchGroup = typeof RESEARCH_GROUPS[number];

/** Scout's saved research: what's new and where voice fits, dated by when it was saved. */
function savedResearchItems(row: BookAccount): ResearchItem[] {
  const research = row.company_research;
  if (!research) return [];
  const savedDay = research.researched_at ? pacificCalendarDay(research.researched_at) : null;
  const items: ResearchItem[] = research.whats_new.map(text => ({ source: 'Scout', day: savedDay, text, url: null }));
  if (research.voice_angle) {
    items.push({ source: 'Scout', day: savedDay, text: `Where voice fits: ${research.voice_angle}`, url: null });
  }

  // Each cited source is its own dated, linked line.
  research.sources.forEach(item => {
    const href = safeHref(item.url);
    items.push({
      source: href ? sourceHost(href) : 'Web', day: item.date ? pacificCalendarDay(item.date) : savedDay,
      text: item.title, url: href,
    });
  });
  return items;
}

/** best_signal.py's weighed facts. `fact` already carries its day in words. The Q4 research's
 * why-now is labelled as such, without the "Q4 research: " its fact opens with. */
function signalItems(row: BookAccount): ResearchItem[] {
  return (row.signals ?? []).map(signal => {
    const fromQ4 = isQ4Signal(signal);
    const source = fromQ4 ? q4SignalLabel(signal.kind) ?? 'Q4 research' : 'Signal';
    const fact = fromQ4 ? withoutQ4Prefix(signal.fact) : signal.fact;
    return { source, day: signal.on, text: fact + (signal.quotable ? '' : ' (internal)'), url: safeHref(signal.url) };
  });
}

/** The Q4 why-now from the record itself, only when no signal already carries it (a payload
 * built before the join added the signal). */
function q4WhyNowItems(row: BookAccount): ResearchItem[] {
  const whyNow = row.q4_target?.why_now;
  if (!whyNow?.text || (row.signals ?? []).some(isQ4Signal)) return [];
  return [{
    source: whyNow.unreviewed ? 'Q4 research (unreviewed Exa news)' : 'Q4 research',
    day: whyNow.on, text: whyNow.text, url: safeHref(whyNow.url),
  }];
}

/** The Clay signal: an overview row (its types, sighting count and seen range), the summary,
 * then each sighting, dated by when each was seen. */
function claySignalItems(row: BookAccount): ResearchItem[] {
  const signal = row.clay?.signal;
  if (!signal) return [];
  const sightings = signal.sightings ?? [];
  const items: ResearchItem[] = [];

  // "Pricing page visits, Docs · 3 sightings, 2026-08-21 – 2026-08-25"
  const overview = [signalTypes(signal).join(', '), signalRange(signal)].filter(Boolean).join(' · ');
  if (overview) items.push({ source: 'Clay', day: signal.last_seen ?? null, text: overview, url: null });

  // The summary only where it says something the sightings and dates have not. A bare URL in it
  // becomes the row's link rather than raw text.
  const summary = signal.summary ? summaryParts(signal.summary, sightings, signal.name) : [];
  const summaryUrl = summary.find(part => /^https?:\/\/\S+$/.test(part)) ?? null;
  const summaryText = summary.filter(part => part !== summaryUrl).join(' — ');
  if (summaryText || summaryUrl) {
    items.push({
      source: 'Clay', day: signal.last_seen ?? null, text: summaryText || 'Signal evidence',
      url: safeHref(summaryUrl),
    });
  }

  sightings.forEach(sighting => {
    items.push({
      source: 'Clay', day: sighting.day,
      text: [sighting.signal, sighting.detail].filter(Boolean).join(': ') || 'Signal seen',
      url: safeHref(sighting.url),
    });
  });
  return items;
}

/** The OB Engine's read as compact lines: the tier and verdict (linked to its evidence), each
 * real score adjustment, and any fact it wrote that has no sentence of its own. */
function clayEngineItems(row: BookAccount): ResearchItem[] {
  const engine = row.clay?.engine;
  if (!engine) return [];
  const day = engine.last_run ?? null;

  // The verdict line, e.g. "Tier 1 · Ships a voice product, found on a page describing the product."
  const verdict = [tierBand(engine), verdictSentence(engine)].filter(Boolean).join(' · ');
  const items: ResearchItem[] = [];
  if (verdict || engine.evidence_url) {
    items.push({ source: 'Clay', day, text: verdict || 'Engine evidence', url: safeHref(engine.evidence_url) });
  }

  // A cap or a promotion, only where one actually happened.
  tierAdjustments(engine.tier_reason ?? '').forEach(adjustment => {
    const text = humanValue(adjustment.text) ?? adjustment.text;
    items.push({ source: 'Clay', day, text: adjustment.kind ? `${adjustment.kind}: ${text}` : text, url: null });
  });

  // Sector and anything new the workflows write, so a new field announces itself.
  const facts = [engine.sector ? `Sector: ${engine.sector}` : null,
    ...Object.entries(engine.extra ?? {}).map(([key, value]) => `${key.replace(/_/g, ' ')}: ${humanValue(value) ?? ''}`)];
  const factText = facts.filter(Boolean).join(' · ');
  if (factText) items.push({ source: 'Clay', day, text: factText, url: null });
  return items;
}

/** Account triage's two written lines, dated by the triage. */
function triageItems(row: BookAccount): ResearchItem[] {
  const items: ResearchItem[] = [];
  if (row.outbound_personalization) {
    items.push({ source: 'Triage', day: row.triaged_on, text: `Opener: ${row.outbound_personalization}`, url: null });
  }
  if (row.use_case_explanation) {
    items.push({ source: 'Triage', day: row.triaged_on, text: `Why Cartesia: ${row.use_case_explanation}`, url: null });
  }
  return items;
}

/** Every research item, newest first; undated items last. */
export function researchItems(row: BookAccount): ResearchItem[] {
  const items = [
    ...savedResearchItems(row), ...signalItems(row), ...q4WhyNowItems(row), ...claySignalItems(row),
    ...clayEngineItems(row), ...triageItems(row),
  ];
  return items.sort((first, second) => (second.day || '').localeCompare(first.day || ''));
}

/** Which recency group a day falls in, against the book's day. Undated is Earlier. */
export function researchGroup(day: string | null, today: string): ResearchGroup {
  if (!day) return 'Earlier';
  // A full timestamp is read as its Pacific calendar day, like the book's own dates. A month-only
  // date ("2026-09", the Q4 research's) counts from the month's first day, never the day before.
  const calendarDay = /^\d{4}-\d{2}$/.test(day) ? `${day}-01` : pacificCalendarDay(day);
  const age = daysSince(calendarDay, new Date(pacificCalendarDay(today) + 'T00:00:00Z'));
  if (age <= 7) return 'This week';
  if (age <= 30) return 'Last 30 days';
  return 'Earlier';
}

/** The items in each group, in group order. Empty groups are kept so the view can say so. */
export function groupResearch(items: ResearchItem[], today: string): { group: ResearchGroup; items: ResearchItem[] }[] {
  return RESEARCH_GROUPS.map(group => ({
    group, items: items.filter(item => researchGroup(item.day, today) === group),
  }));
}

/** The source filter's options: each source once, in first-seen order. */
export function researchSources(items: ResearchItem[]): string[] {
  return [...new Set(items.map(item => item.source))];
}
